// ─────────────────────────────────────────────────────────────────────────
// Menu Import Service — safe bulk-update of menu items from a CSV file.
//
// Implements the Phase-2 requirements from 2026-04-24:
//   1. RFC 4180 CSV parser (matches the exporter's format exactly)
//   2. Diff engine: add / update / delete / unchanged classification
//   3. Validation: reject bad rows before the user can apply changes
//   4. Auto-backup to menu_backups/{timestamp} BEFORE any write
//   5. Undo: restore from the most recent backup
//
// Matching strategy, most reliable → least:
//   (a) Firestore ID column present      → direct id match
//   (b) "Item Number" column present     → substring match in name prefix
//   (c) Name (EN) match (case-insensitive) as last resort
//
// Fields the import DOES touch (mirrors the exporter's columns):
//   name, name_de, description, description_de, category, brand,
//   price_with_tax, base_price, tax_rate, is_available, sold_out,
//   is_vegetarian, is_vegan, is_popular, image_url
//
// Fields the import does NOT touch (preserved from Firestore):
//   display_order, display_order_by_brand, modifier_group_ids,
//   sold_out_at, sold_out_by, created_at
// ─────────────────────────────────────────────────────────────────────────

import firebaseAdminService, { AdminMenuItem } from './firebaseAdminService';

export interface CsvRow {
  raw: Record<string, string>;
  lineNumber: number;
  firestoreId?: string;
  itemNumber?: string;
  nameEn: string;
  nameDe?: string;
  descriptionEn?: string;
  descriptionDe?: string;
  category: string;
  brand?: 'saheeh' | 'pizza_brand' | 'both' | null;
  priceInclTax: number;
  basePrice?: number;
  taxRate?: number;
  available: boolean;
  soldOut: boolean;
  vegetarian: boolean;
  vegan: boolean;
  popular: boolean;
  imageUrl: string;
}

export interface ValidationError {
  lineNumber: number;
  field: string;
  message: string;
  rawValue?: string;
}

export type DiffAction = 'add' | 'update' | 'delete' | 'unchanged';

export interface DiffRow {
  action: DiffAction;
  csvRow?: CsvRow;
  existingItem?: AdminMenuItem;
  changes?: Array<{ field: string; oldValue: any; newValue: any }>;
  // Conflict: existing item was modified server-side after the CSV export.
  conflict?: { serverUpdatedAt: string; description: string };
}

export interface DiffReport {
  diff: DiffRow[];
  stats: { add: number; update: number; delete: number; unchanged: number; conflicts: number };
  validationErrors: ValidationError[];
}

// ── CSV parser ────────────────────────────────────────────────────────────
// RFC 4180 compliant. Handles:
//   - quoted fields with commas inside
//   - escaped quotes ("") inside quoted fields
//   - CRLF or LF line endings
//   - UTF-8 BOM stripping at file start
function parseCsv(text: string): string[][] {
  // Strip UTF-8 BOM (Excel on Windows always adds this when saving "CSV UTF-8").
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  // Honour Excel's "sep=X" directive. German/EU Windows Excel writes a line
  // like `sep=;` at the very top so other copies of Excel open it with the
  // right delimiter. We parse it, use it, and strip it so it doesn't leak into
  // the data rows.
  let forcedDelim: string | null = null;
  const sepMatch = text.match(/^sep=(.)\r?\n/i);
  if (sepMatch) {
    forcedDelim = sepMatch[1];
    text = text.slice(sepMatch[0].length);
  }

  // Auto-detect the delimiter by sampling the first non-empty line (outside
  // quotes) and picking whichever of comma / semicolon / tab / pipe appears
  // most often. German Windows Excel defaults to `;`; Excel "Text" export to
  // tab. If none found, fall back to `,`.
  const pickDelimiter = (): string => {
    if (forcedDelim) return forcedDelim;
    let inQ = false;
    const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0, '|': 0 };
    for (let k = 0; k < text.length; k++) {
      const c = text[k];
      if (c === '"') { inQ = !inQ; continue; }
      if (inQ) continue;
      if (c === '\n') break; // first line only
      if (counts[c] !== undefined) counts[c]++;
    }
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return best && best[1] > 0 ? best[0] : ',';
  };
  const delim = pickDelimiter();

  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === delim) { row.push(field); field = ''; i++; continue; }
    if (ch === '\r') { i++; continue; }
    if (ch === '\n') {
      row.push(field);
      rows.push(row);
      field = '';
      row = [];
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  // Flush last field / row
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop trailing empty rows
  while (rows.length > 0 && rows[rows.length - 1].every(f => f === '')) rows.pop();
  return rows;
}

// ── Header mapping ────────────────────────────────────────────────────────
// Accept a few common header spellings so users can tweak the file slightly.
const HEADER_MAP: Record<string, string> = {
  // English
  'firestore id': 'firestoreId',
  'id': 'firestoreId',
  'item number': 'itemNumber',
  'item id': 'itemNumber',
  'item no': 'itemNumber',
  'name (en)': 'nameEn',
  'name en': 'nameEn',
  'name': 'nameEn',
  'name (de)': 'nameDe',
  'name de': 'nameDe',
  'german name': 'nameDe',
  'description (en)': 'descriptionEn',
  'description en': 'descriptionEn',
  'description': 'descriptionEn',
  'description (de)': 'descriptionDe',
  'description de': 'descriptionDe',
  'category': 'category',
  'brand': 'brand',
  'price (incl. tax)': 'priceInclTax',
  'price incl tax': 'priceInclTax',
  'price': 'priceInclTax',
  'base price (excl. tax)': 'basePrice',
  'base price excl tax': 'basePrice',
  'base price': 'basePrice',
  'tax rate %': 'taxRate',
  'tax rate': 'taxRate',
  'available': 'available',
  'sold out': 'soldOut',
  'vegetarian': 'vegetarian',
  'vegan': 'vegan',
  'popular': 'popular',
  'image url': 'imageUrl',
  'image': 'imageUrl',
  // German (client's staff may edit with German headers in Excel)
  'artikelnummer': 'itemNumber',
  'artikel nr': 'itemNumber',
  'nr': 'itemNumber',
  'name (englisch)': 'nameEn',
  'name (deutsch)': 'nameDe',
  'beschreibung (en)': 'descriptionEn',
  'beschreibung (de)': 'descriptionDe',
  'beschreibung (englisch)': 'descriptionEn',
  'beschreibung (deutsch)': 'descriptionDe',
  'beschreibung': 'descriptionDe',
  'kategorie': 'category',
  'marke': 'brand',
  'preis (inkl. mwst.)': 'priceInclTax',
  'preis (inkl mwst)': 'priceInclTax',
  'preis inkl. mwst.': 'priceInclTax',
  'preis inkl mwst': 'priceInclTax',
  'preis': 'priceInclTax',
  'nettopreis': 'basePrice',
  'grundpreis': 'basePrice',
  'mwst %': 'taxRate',
  'mwst': 'taxRate',
  'steuersatz': 'taxRate',
  'verfügbar': 'available',
  'verfuegbar': 'available',
  'ausverkauft': 'soldOut',
  'vegetarisch': 'vegetarian',
  'beliebt': 'popular',
  'bild url': 'imageUrl',
  'bild': 'imageUrl',
  'bildlink': 'imageUrl',
};

function parseBool(v: any): boolean {
  if (typeof v === 'boolean') return v;
  const s = String(v || '').toLowerCase().trim();
  // English + German truthy values (client's staff edits in German Excel).
  return s === 'yes' || s === 'y' || s === 'true' || s === '1' || s === 'ja' || s === 'j' || s === 'wahr' || s === 'x';
}

function parseNumber(v: any): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  // Accept both "9.90" and "9,90" (German decimals)
  const s = String(v).replace(',', '.').replace(/[^\d.\-]/g, '');
  const n = parseFloat(s);
  return isFinite(n) ? n : undefined;
}

export interface ParseResult {
  rows: CsvRow[];
  validationErrors: ValidationError[];
  headerWarnings: string[];
}

export function parseMenuCsv(text: string): ParseResult {
  const errors: ValidationError[] = [];
  const warnings: string[] = [];
  const raw = parseCsv(text);
  if (raw.length < 2) {
    errors.push({ lineNumber: 0, field: 'file', message: 'CSV file is empty or has no data rows.' });
    return { rows: [], validationErrors: errors, headerWarnings: warnings };
  }

  // Apple Numbers (and some Excel templates) put a table title in row 1,
  // pushing the real header row to row 2. Auto-detect by scanning the first
  // few rows for the one that actually contains known column names — the row
  // with the most HEADER_MAP hits wins. Falls back to row 0 if nothing looks
  // like a header (so the original error message still fires).
  const scanLimit = Math.min(5, raw.length);
  let headerRowIdx = 0;
  let bestHits = -1;
  for (let i = 0; i < scanLimit; i++) {
    const candidate = raw[i].map(h => (h || '').trim().toLowerCase());
    const hits = candidate.filter(h => HEADER_MAP[h] !== undefined).length;
    if (hits > bestHits) {
      bestHits = hits;
      headerRowIdx = i;
    }
  }
  const header = raw[headerRowIdx].map(h => (h || '').trim().toLowerCase());
  const fieldIndex: Record<string, number> = {};
  header.forEach((h, idx) => {
    const key = HEADER_MAP[h];
    if (key && fieldIndex[key] === undefined) fieldIndex[key] = idx;
  });
  if (headerRowIdx > 0) {
    warnings.push(`Skipped ${headerRowIdx} title row(s) before the header — detected headers on CSV line ${headerRowIdx + 1}.`);
  }

  // Required columns
  const requiredFields = ['nameEn', 'category', 'priceInclTax'];
  for (const f of requiredFields) {
    if (fieldIndex[f] === undefined) {
      errors.push({ lineNumber: 1, field: f, message: `Missing required column: ${f}` });
    }
  }
  if (errors.length > 0) return { rows: [], validationErrors: errors, headerWarnings: warnings };

  // Sanity-check unknown headers
  header.forEach((h) => {
    if (h && !HEADER_MAP[h]) warnings.push(`Unknown column "${h}" — will be ignored.`);
  });

  const rows: CsvRow[] = [];
  for (let i = headerRowIdx + 1; i < raw.length; i++) {
    const line = raw[i];
    const lineNumber = i + 1;
    if (line.every(f => f.trim() === '')) continue;

    const get = (key: string): string => {
      const idx = fieldIndex[key];
      if (idx === undefined) return '';
      return (line[idx] || '').trim();
    };

    // Accept rows that have either EN or DE name filled. We mirror the missing
    // side so storefronts never render blank names — the client can enrich the
    // other language later. Only reject if both are empty.
    const rawNameEn = get('nameEn');
    const rawNameDe = get('nameDe');
    const nameEn = rawNameEn || rawNameDe;
    const category = get('category');
    const priceNum = parseNumber(get('priceInclTax'));

    // Row-level validation
    if (!nameEn) {
      errors.push({ lineNumber, field: 'Name (EN)', message: 'Name cannot be empty (fill EN or DE).' });
      continue;
    }
    if (!category) {
      errors.push({ lineNumber, field: 'Category', message: 'Category cannot be empty.' });
      continue;
    }
    if (priceNum === undefined) {
      errors.push({ lineNumber, field: 'Price', message: `Price "${get('priceInclTax')}" is not a valid number.` });
      continue;
    }
    if (priceNum < 0) {
      errors.push({ lineNumber, field: 'Price', message: `Price "${priceNum}" cannot be negative.` });
      continue;
    }

    const brandRaw = get('brand').toLowerCase();
    let brand: CsvRow['brand'] = null;
    if (brandRaw === 'saheeh') brand = 'saheeh';
    else if (brandRaw === 'pizza_brand' || brandRaw === 'pizza_brand') brand = 'pizza_brand';
    else if (brandRaw === 'both') brand = 'both';
    // "Auto (derive from category)" or anything else → null (derive at runtime)

    rows.push({
      raw: Object.fromEntries(header.map((h, idx) => [h, line[idx] || ''])),
      lineNumber,
      firestoreId: get('firestoreId') || undefined,
      itemNumber: get('itemNumber') || undefined,
      nameEn,
      nameDe: rawNameDe || rawNameEn || undefined,
      descriptionEn: get('descriptionEn') || get('descriptionDe') || undefined,
      descriptionDe: get('descriptionDe') || get('descriptionEn') || undefined,
      category,
      brand,
      priceInclTax: priceNum,
      basePrice: parseNumber(get('basePrice')),
      taxRate: parseNumber(get('taxRate')),
      available: get('available') === '' ? true : parseBool(get('available')),
      soldOut: parseBool(get('soldOut')),
      vegetarian: parseBool(get('vegetarian')),
      vegan: parseBool(get('vegan')),
      popular: parseBool(get('popular')),
      imageUrl: get('imageUrl'),
    });
  }

  // Duplicate-key detection INSIDE the CSV. Only Firestore ID is a hard block
  // — two rows with the same ID would overwrite the same document and one
  // edit would silently disappear. Item Number is a cosmetic prefix on the
  // name (e.g. "41 Avo Maki"); in real menus two different dishes can share
  // a number without breaking anything, so we just warn rather than block.
  const seenIds = new Map<string, number>();
  const seenItemNumbers = new Map<string, number>();
  rows.forEach((r) => {
    if (r.firestoreId) {
      const prev = seenIds.get(r.firestoreId);
      if (prev !== undefined) {
        errors.push({ lineNumber: r.lineNumber, field: 'Firestore ID', message: `Duplicate Firestore ID "${r.firestoreId}" — first seen on line ${prev}. IDs must be unique.` });
      } else {
        seenIds.set(r.firestoreId, r.lineNumber);
      }
    }
    if (r.itemNumber) {
      const prev = seenItemNumbers.get(r.itemNumber);
      if (prev !== undefined) {
        warnings.push(`Line ${r.lineNumber}: Item Number "${r.itemNumber}" is also used on line ${prev}. Import will still proceed (matching uses Firestore ID), but consider renumbering to avoid confusion on receipts.`);
      } else {
        seenItemNumbers.set(r.itemNumber, r.lineNumber);
      }
    }
  });

  return { rows, validationErrors: errors, headerWarnings: warnings };
}

// ── Diff engine ───────────────────────────────────────────────────────────
// Compares CSV rows against the current menu and produces an action plan.
// `validCategories` is used to flag rows pointing at a non-existent category.
export function buildDiff(
  csvRows: CsvRow[],
  existing: AdminMenuItem[],
  validCategories: string[],
  exportTimestamp?: Date,
): DiffReport {
  const extraErrors: ValidationError[] = [];
  const diff: DiffRow[] = [];
  const catSet = new Set(validCategories.map(c => c.toLowerCase()));

  // Validate category existence for each CSV row.
  csvRows.forEach((row) => {
    if (!catSet.has(row.category.toLowerCase())) {
      extraErrors.push({
        lineNumber: row.lineNumber,
        field: 'Category',
        message: `Category "${row.category}" does not exist — create it first or fix the spelling.`,
      });
    }
  });

  // Index existing by various keys for matching.
  const byId = new Map<string, AdminMenuItem>();
  const byItemNumber = new Map<string, AdminMenuItem>();
  const byNameLower = new Map<string, AdminMenuItem>();
  const extractItemNumber = (name: string): string => {
    const match = (name || '').match(/^(\d+)[\s.:\-—]/);
    return match ? match[1] : '';
  };
  existing.forEach((item) => {
    byId.set(item.id, item);
    const num = extractItemNumber(item.name);
    if (num) byItemNumber.set(num, item);
    byNameLower.set((item.name || '').toLowerCase().trim(), item);
  });

  const matchedIds = new Set<string>();

  csvRows.forEach((row) => {
    // Find matching existing item — 3-tier fallback
    let match: AdminMenuItem | undefined;
    if (row.firestoreId && byId.has(row.firestoreId)) match = byId.get(row.firestoreId);
    if (!match && row.itemNumber && byItemNumber.has(row.itemNumber)) match = byItemNumber.get(row.itemNumber);
    if (!match) match = byNameLower.get(row.nameEn.toLowerCase().trim());

    if (!match) {
      diff.push({ action: 'add', csvRow: row });
      return;
    }

    matchedIds.add(match.id);

    // Compute the would-be updated item and diff field by field.
    const taxRate = row.taxRate ?? match.tax_rate ?? 7;
    const priceInclTax = row.priceInclTax;
    const basePrice = row.basePrice ?? (taxRate ? priceInclTax / (1 + taxRate / 100) : priceInclTax);
    const newBrand: CsvRow['brand'] = row.brand;

    const changes: DiffRow['changes'] = [];
    const cmpNum = (a: any, b: any) => Math.round((Number(a) || 0) * 100) !== Math.round((Number(b) || 0) * 100);
    const cmpStr = (a: any, b: any) => (a || '').toString().trim() !== (b || '').toString().trim();
    const cmpBool = (a: any, b: any) => Boolean(a) !== Boolean(b);

    if (cmpStr(match.name, row.nameEn)) changes.push({ field: 'name', oldValue: match.name, newValue: row.nameEn });
    if (cmpStr(match.name_de || '', row.nameDe || '')) changes.push({ field: 'name_de', oldValue: match.name_de, newValue: row.nameDe });
    if (cmpStr(match.description || '', row.descriptionEn || '')) changes.push({ field: 'description', oldValue: match.description, newValue: row.descriptionEn });
    if (cmpStr(match.description_de || '', row.descriptionDe || '')) changes.push({ field: 'description_de', oldValue: match.description_de, newValue: row.descriptionDe });
    if (cmpStr(match.category, row.category)) changes.push({ field: 'category', oldValue: match.category, newValue: row.category });
    if ((match.brand || null) !== (newBrand || null)) changes.push({ field: 'brand', oldValue: match.brand, newValue: newBrand });
    if (cmpNum(match.price_with_tax ?? match.price, priceInclTax)) changes.push({ field: 'price_with_tax', oldValue: match.price_with_tax ?? match.price, newValue: priceInclTax });
    if (cmpNum(match.base_price, basePrice)) changes.push({ field: 'base_price', oldValue: match.base_price, newValue: basePrice });
    if (cmpNum(match.tax_rate, taxRate)) changes.push({ field: 'tax_rate', oldValue: match.tax_rate, newValue: taxRate });
    if (cmpBool(match.is_available, row.available)) changes.push({ field: 'is_available', oldValue: match.is_available, newValue: row.available });
    if (cmpBool(match.sold_out, row.soldOut)) changes.push({ field: 'sold_out', oldValue: match.sold_out, newValue: row.soldOut });
    if (cmpBool(match.is_vegetarian, row.vegetarian)) changes.push({ field: 'is_vegetarian', oldValue: match.is_vegetarian, newValue: row.vegetarian });
    if (cmpBool(match.is_vegan, row.vegan)) changes.push({ field: 'is_vegan', oldValue: match.is_vegan, newValue: row.vegan });
    if (cmpBool(match.is_popular, row.popular)) changes.push({ field: 'is_popular', oldValue: match.is_popular, newValue: row.popular });
    if (cmpStr(match.image_url || '', row.imageUrl || '')) changes.push({ field: 'image_url', oldValue: match.image_url, newValue: row.imageUrl });

    const row_: DiffRow = {
      action: changes.length === 0 ? 'unchanged' : 'update',
      csvRow: row,
      existingItem: match,
      changes,
    };

    // Conflict check — did the server-side doc change since the export?
    if (exportTimestamp && match.updated_at) {
      const serverTs = new Date(match.updated_at);
      if (isFinite(serverTs.getTime()) && serverTs > exportTimestamp) {
        row_.conflict = {
          serverUpdatedAt: match.updated_at,
          description: `Server version was modified ${formatRelativeTime(serverTs)} after your export — your upload will overwrite those changes.`,
        };
      }
    }

    diff.push(row_);
  });

  // Anything in existing not matched by CSV → marked for deletion.
  existing.forEach((item) => {
    if (!matchedIds.has(item.id)) {
      diff.push({ action: 'delete', existingItem: item });
    }
  });

  const stats = {
    add: diff.filter(d => d.action === 'add').length,
    update: diff.filter(d => d.action === 'update').length,
    delete: diff.filter(d => d.action === 'delete').length,
    unchanged: diff.filter(d => d.action === 'unchanged').length,
    conflicts: diff.filter(d => d.conflict).length,
  };

  return { diff, stats, validationErrors: extraErrors };
}

function formatRelativeTime(d: Date): string {
  const ms = Date.now() - d.getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// ── Apply, backup, restore ─────────────────────────────────────────────────
export interface ApplyOptions {
  skipDeletes?: boolean;         // if true, don't delete items missing from CSV
  skipConflicts?: boolean;       // if true, don't write rows with conflicts
  backupAuthor?: string;         // admin email that ran the import
  sourceFilename?: string;
}

export interface BackupMetadata {
  id: string;
  created_at: string;
  item_count: number;
  author?: string;
  source_filename?: string;
  reason: 'menu_import' | 'manual';
}

export async function backupCurrentMenu(
  existing: AdminMenuItem[],
  meta: Omit<BackupMetadata, 'id' | 'created_at' | 'item_count'>,
): Promise<string> {
  const { collection, doc, setDoc, serverTimestamp } = await import('../services/firestoreCompat');
  const { db } = await import('./firebaseConfig');

  const now = new Date();
  const backupId = `backup_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

  const backupRef = doc(collection(db, 'menu_backups'), backupId);
  // Firestore rejects `undefined` field values. Some historical menu items
  // have optional fields left unset (e.g. name_de, image_url, modifier groups)
  // which come back as `undefined` from the admin service. We recursively
  // strip them before writing the backup so setDoc accepts the payload.
  const stripUndefined = (val: any): any => {
    if (val === undefined) return null;
    if (val === null) return null;
    if (Array.isArray(val)) return val.map(stripUndefined);
    if (typeof val === 'object') {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(val)) {
        if (v === undefined) continue; // drop the key entirely
        out[k] = stripUndefined(v);
      }
      return out;
    }
    return val;
  };
  const snapshot = existing.map((item) => stripUndefined({ ...item }));

  await setDoc(backupRef, {
    id: backupId,
    created_at: serverTimestamp(),
    item_count: existing.length,
    author: meta.author || '',
    source_filename: meta.source_filename || '',
    reason: meta.reason,
    snapshot,
  });

  console.log(`📦 [MENU BACKUP] Wrote ${existing.length} items → menu_backups/${backupId}`);
  return backupId;
}

export async function listRecentBackups(limitCount = 10): Promise<BackupMetadata[]> {
  const { collection, query, orderBy, limit, getDocs } = await import('../services/firestoreCompat');
  const { db } = await import('./firebaseConfig');
  const snap = await getDocs(query(collection(db, 'menu_backups'), orderBy('created_at', 'desc'), limit(limitCount)));
  const out: BackupMetadata[] = [];
  snap.forEach((d) => {
    const data = d.data() as any;
    out.push({
      id: d.id,
      created_at: data.created_at?.toDate?.()?.toISOString() || data.created_at || '',
      item_count: data.item_count || 0,
      author: data.author || '',
      source_filename: data.source_filename || '',
      reason: data.reason || 'manual',
    });
  });
  return out;
}

export async function applyImport(
  diff: DiffRow[],
  opts: ApplyOptions,
): Promise<{ added: number; updated: number; deleted: number; skipped: number; errors: string[] }> {
  const errors: string[] = [];
  let added = 0, updated = 0, deleted = 0, skipped = 0;

  for (const row of diff) {
    if (opts.skipConflicts && row.conflict) { skipped++; continue; }

    try {
      if (row.action === 'add' && row.csvRow) {
        const taxRate = row.csvRow.taxRate ?? 7;
        // Firestore refuses `undefined` on setDoc. Build the payload and then
        // drop any undefined fields so an empty CSV cell never reaches the
        // backend as `undefined`. Null and empty-string are still allowed.
        const raw: Record<string, any> = {
          name: row.csvRow.nameEn,
          name_de: row.csvRow.nameDe || row.csvRow.nameEn,
          description: row.csvRow.descriptionEn || '',
          description_de: row.csvRow.descriptionDe || '',
          category: row.csvRow.category,
          brand: row.csvRow.brand,
          price_with_tax: row.csvRow.priceInclTax,
          tax_rate: taxRate,
          is_available: row.csvRow.available,
          sold_out: row.csvRow.soldOut,
          is_vegetarian: row.csvRow.vegetarian,
          is_vegan: row.csvRow.vegan,
          is_popular: row.csvRow.popular,
          image_url: row.csvRow.imageUrl,
        };
        const clean: Record<string, any> = {};
        for (const [k, v] of Object.entries(raw)) if (v !== undefined) clean[k] = v;
        await firebaseAdminService.createMenuItem(clean as any);
        added++;
      } else if (row.action === 'update' && row.existingItem && row.csvRow && row.changes && row.changes.length > 0) {
        const patch: Partial<AdminMenuItem> = {};
        for (const c of row.changes) {
          if (c.newValue !== undefined) (patch as any)[c.field] = c.newValue;
        }
        await firebaseAdminService.updateMenuItem(row.existingItem.id, patch);
        updated++;
      } else if (row.action === 'delete' && row.existingItem && !opts.skipDeletes) {
        await firebaseAdminService.deleteMenuItem(row.existingItem.id);
        deleted++;
      } else {
        skipped++;
      }
    } catch (e: any) {
      errors.push(`${row.csvRow?.nameEn || row.existingItem?.name || '?'}: ${e?.message || 'unknown'}`);
    }
  }

  return { added, updated, deleted, skipped, errors };
}

// ── Restore from backup (undo) ─────────────────────────────────────────────
// Strategy: fetch backup snapshot, then for each item in snapshot:
//   - if it still exists in Firestore → updateMenuItem with all fields
//   - else                             → createMenuItem (with preserved fields)
// Then for each Firestore item NOT in snapshot → deleteMenuItem.
//
// Result: the menu ends up exactly as it was when the backup was taken.
export async function restoreBackup(backupId: string): Promise<{ restored: number; errors: string[] }> {
  const { doc, getDoc } = await import('../services/firestoreCompat');
  const { db } = await import('./firebaseConfig');
  const snap = await getDoc(doc(db, 'menu_backups', backupId));
  if (!snap.exists()) throw new Error(`Backup ${backupId} not found.`);
  const data = snap.data() as any;
  const snapshot: AdminMenuItem[] = data.snapshot || [];
  const snapshotIds = new Set(snapshot.map(s => s.id));

  const current = await firebaseAdminService.getMenuItems();
  const errors: string[] = [];
  let restored = 0;

  // Delete items that exist now but weren't in the backup.
  for (const cur of current) {
    if (!snapshotIds.has(cur.id)) {
      try { await firebaseAdminService.deleteMenuItem(cur.id); }
      catch (e: any) { errors.push(`delete ${cur.name}: ${e?.message}`); }
    }
  }

  // Restore every item in the snapshot.
  for (const item of snapshot) {
    try {
      const existing = current.find(c => c.id === item.id);
      const { id: _id, ...payload } = item;
      if (existing) {
        await firebaseAdminService.updateMenuItem(item.id, payload as Partial<AdminMenuItem>);
      } else {
        await firebaseAdminService.createMenuItem(payload as Partial<AdminMenuItem>);
      }
      restored++;
    } catch (e: any) {
      errors.push(`restore ${item.name}: ${e?.message}`);
    }
  }

  console.log(`♻️ [MENU RESTORE] ${restored}/${snapshot.length} items restored from ${backupId}`);
  return { restored, errors };
}
